import {
  getAIRunsUsageQuery,
  getClientConnectionsUsageQuery,
  getContextsClientsideUsageQuery,
  getContextsServersideUsageQuery,
  getContextsTotalUsageQuery,
  getDataExportEventsUsageQuery,
  getExperimentationEventsUsageQuery,
  getExperimentationKeysUsageQuery,
  getMAUClientsideUsageQuery,
  getMAUTotalUsageQuery,
  getObservabilityErrorsUsageQuery,
  getObservabilityLogsUsageQuery,
  getObservabilityMetricsUsageQuery,
  getObservabilitySessionsUsageQuery,
  getObservabilityTracesUsageQuery,
  getPeakConcurrentConnectionsUsageBySdkVersionQuery,
  getPeakConcurrentConnectionsUsageQuery,
  getSdkActiveQuery,
  getServiceConnectionsUsageQuery,
  getVegaAIUsageQuery,
  getWarehouseExportUsageQuery,
} from '@gonfalon/rest-api/queries/usage';
import { assertNever } from '@gonfalon/types/assertNever';
import type { UseSuspenseQueryOptions } from '@tanstack/react-query';

import {
  type AggregationType,
  type DimensionType,
  type Granularity,
  type UsageQueryFnName,
  type UsageSeriesList,
} from '../../types';

type QueryFn = (params: {
  apiVersion: string;
  query: {
    aggregationType?: AggregationType;
    from: string;
    to: string;
    groupBy?: DimensionType[];
    granularity?: Granularity;
  };
}) => UseSuspenseQueryOptions<UsageSeriesList>;

export function resolveQueryFn(name: UsageQueryFnName | undefined): QueryFn {
  if (name === undefined) {
    throw new Error('This usage tab has no single query function; use its combined-data hook');
  }
  let fn;
  switch (name) {
    case 'getServiceConnectionsUsageQuery':
      fn = getServiceConnectionsUsageQuery;
      break;
    case 'getMAUClientsideUsageQuery':
      fn = getMAUClientsideUsageQuery;
      break;
    case 'getDataExportEventsUsageQuery':
      fn = getDataExportEventsUsageQuery;
      break;
    case 'getWarehouseExportUsageQuery':
      fn = getWarehouseExportUsageQuery;
      break;
    case 'getExperimentationKeysUsageQuery':
      fn = getExperimentationKeysUsageQuery;
      break;
    case 'getExperimentationEventsUsageQuery':
      fn = getExperimentationEventsUsageQuery;
      break;
    case 'getMAUTotalUsageQuery':
      fn = getMAUTotalUsageQuery;
      break;
    case 'getSdkActiveQuery':
      fn = getSdkActiveQuery;
      break;
    case 'getContextsTotalUsageQuery':
      fn = getContextsTotalUsageQuery;
      break;
    case 'getContextsClientsideUsageQuery':
      fn = getContextsClientsideUsageQuery;
      break;
    case 'getContextsServersideUsageQuery':
      fn = getContextsServersideUsageQuery;
      break;
    case 'getObservabilitySessionsUsageQuery':
      fn = getObservabilitySessionsUsageQuery;
      break;
    case 'getObservabilityErrorsUsageQuery':
      fn = getObservabilityErrorsUsageQuery;
      break;
    case 'getObservabilityLogsUsageQuery':
      fn = getObservabilityLogsUsageQuery;
      break;
    case 'getObservabilityTracesUsageQuery':
      fn = getObservabilityTracesUsageQuery;
      break;
    case 'getObservabilityMetricsUsageQuery':
      fn = getObservabilityMetricsUsageQuery;
      break;
    case 'getVegaAIUsageQuery':
      fn = getVegaAIUsageQuery;
      break;
    case 'getAIRunsUsageQuery':
      fn = getAIRunsUsageQuery;
      break;
    case 'getClientConnectionsUsageQuery':
      fn = getClientConnectionsUsageQuery;
      break;
    case 'getPeakConcurrentConnectionsUsageQuery':
      fn = getPeakConcurrentConnectionsUsageQuery;
      break;
    case 'getPeakConcurrentConnectionsUsageBySdkVersionQuery':
      fn = getPeakConcurrentConnectionsUsageBySdkVersionQuery;
      break;
    default:
      assertNever(name);
  }

  if (typeof fn !== 'function') {
    throw new Error(`Unknown query function: ${name}`);
  }
  return fn as QueryFn;
}
